import { useState, useMemo } from "react";
import { LayoutGrid, GitCommitVertical } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { Reveal, EASE } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import PartnerWall from "@/components/PartnerWall";
import SpotlightSection from "@/components/SpotlightSection";
import { Flag, Activities, VentureMeta, PartnerNetwork } from "@/components/VenturePieces";
import Img from "@/components/Img";
import HEROES from "@/lib/pageHeroes.json";

// Dubai — Marina skyline under development

function TimelineItem({ venture, index }) {
  const { t, L } = useLang();
  return (
    <Reveal delay={0.06 * index} className="relative ps-10 md:ps-16">
      <span className="absolute start-0 top-2 flex h-4 w-4 -translate-x-[7px] items-center justify-center rounded-full bg-primary rtl:translate-x-[7px]">
        <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
      </span>
      <div data-testid={`venture-detail-${venture.id}`} className="pb-16 md:pb-20">
        <p className="eyebrow" data-testid={`venture-date-${venture.id}`}>{venture.date}</p>
        <div className="mt-3 flex flex-wrap items-baseline gap-x-5 gap-y-2">
          <h2 className="font-head text-3xl md:text-5xl leading-none">{venture.name}</h2>
          <span className="rounded-full border border-primary/40 px-3 py-1 text-[10px] tracking-[0.18em] uppercase text-primary">
            {L(venture.badge)}
          </span>
        </div>
        <div className="mt-7 grid gap-7 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="relative h-[200px] overflow-hidden border border-border md:h-[260px]" style={{ borderRadius: "var(--radius)" }}>
              <Img src={venture.image} width={960} sizes="(max-width: 768px) 100vw, 42vw" alt={venture.name} className="h-full w-full object-cover transition-transform duration-[1400ms] hover:scale-105" />
              <span className="absolute bottom-4 start-4 flex items-center gap-2 rounded-full bg-black/45 px-3 py-1.5 text-[10px] tracking-[0.15em] uppercase text-white backdrop-blur-sm">
                <Flag code={venture.partner_code} className="h-3 w-4" /> {venture.region}
              </span>
            </div>
          </div>
          <div className="md:col-span-7">
            <p className="text-base text-foreground/85 leading-relaxed">{L(venture.focus)}</p>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{L(venture.description)}</p>
            <Activities items={venture.activities} />
            <PartnerNetwork names={venture.partner_network} testid={`venture-network-${venture.id}`} />
            <p className="mt-6 text-xs text-muted-foreground">
              {t("jv_with")} <span className="text-foreground/80">{venture.partner}</span> — {venture.partner_origin}
            </p>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

function GridItem({ venture, index }) {
  const { L } = useLang();
  return (
    <Reveal delay={0.06 * index}>
      <article data-testid={`venture-detail-${venture.id}`} className="h-full overflow-hidden border border-border card-lift" style={{ borderRadius: "var(--radius)" }}>
        <div className="relative h-[220px]">
          <Img src={venture.image} width={960} sizes="(max-width: 768px) 100vw, 50vw" alt={venture.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/35 to-transparent" />
          <span className="absolute bottom-5 start-6 rounded-full bg-primary px-4 py-1.5 text-[11px] font-bold tracking-[0.2em] uppercase text-primary-foreground" data-testid={`venture-date-${venture.id}`}>
            {venture.date}
          </span>
        </div>
        <div className="p-7 md:p-9">
          <h2 className="font-head text-3xl md:text-4xl">{venture.name}</h2>
          <p className="eyebrow mt-2">{L(venture.badge)}</p>
          <p className="mt-5 text-base text-foreground/85 leading-relaxed">{L(venture.focus)}</p>
          <Activities items={venture.activities} />
          <PartnerNetwork names={venture.partner_network} testid={`venture-network-grid-${venture.id}`} />
          <VentureMeta venture={venture} />
        </div>
      </article>
    </Reveal>
  );
}

export default function Ventures() {
  const { t } = useLang();
  const site = useSite();
  const ventures = site?.ventures || [];
  const [view, setView] = useState("timeline");
  const [region, setRegion] = useState("all");

  const regions = useMemo(() => [...new Set(ventures.map((v) => v.region).filter(Boolean))], [ventures]);
  const slug = (r) => r.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  const shown = region === "all" ? ventures : ventures.filter((v) => v.region === region);

  const views = [
    { id: "timeline", label: t("jv_view_timeline"), Icon: GitCommitVertical },
    { id: "grid", label: t("jv_view_grid"), Icon: LayoutGrid },
  ];

  return (
    <div data-testid="ventures-page">
      <Seo page="ventures" />
      <PageHero eyebrow={t("ventures_page_eyebrow")} title={t("ventures_page_title")} sub={t("ventures_sub")} image={HEROES["ventures"]} />

      <SpotlightSection placement="show_ventures" compact />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 md:py-24">
        <div className="mb-14 flex flex-wrap items-center justify-between gap-5 border-b border-border pb-6">
          <div className="flex flex-wrap gap-2" data-testid="jv-region-filters">
            {[{ id: "all", label: t("jv_filter_all") }, ...regions.map((r) => ({ id: r, label: r }))].map((r) => (
              <button
                key={r.id}
                type="button"
                data-testid={`jv-region-${r.id === "all" ? "all" : slug(r.id)}`}
                onClick={() => setRegion(r.id)}
                className={`h-9 rounded-full px-4 text-xs font-semibold tracking-wide transition-colors ${
                  region === r.id ? "bg-primary text-primary-foreground" : "border border-border text-foreground/70 hover:text-primary"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1 rounded-full border border-border p-1" data-testid="jv-view-toggle">
            {views.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                data-testid={`jv-view-${id}`}
                onClick={() => setView(id)}
                className={`inline-flex h-8 items-center gap-2 rounded-full px-4 text-xs font-semibold transition-colors ${
                  view === id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-primary"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
        </div>

        {shown.length === 0 && <p className="py-20 text-center text-sm text-muted-foreground" data-testid="jv-empty">{t("jv_none")}</p>}

        <AnimatePresence mode="wait">
          <motion.div
            key={`${view}-${region}`}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: EASE }}
          >
            {view === "timeline" ? (
              <div className="relative border-s border-border" data-testid="jv-timeline">
                {shown.map((v, i) => (
                  <TimelineItem key={v.id} venture={v} index={i} />
                ))}
              </div>
            ) : (
              <div className="grid gap-7 lg:grid-cols-2" data-testid="jv-grid">
                {shown.map((v, i) => (
                  <GridItem key={v.id} venture={v} index={i} />
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </section>

      <PartnerWall />
    </div>
  );
}
